import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const IPMPlanGenerator = ({ onCalculationComplete }) => {
  const [formData, setFormData] = useState({
    cropType: '',
    growingSystem: 'indoor',
    facilitySize: '',
    regionClimate: 'temperate',
    currentPests: [],
    historicalIssues: [],
    budgetRange: 'medium',
    certificationRequirements: [],
    harvestSchedule: 'continuous',
    experienceLevel: 'intermediate'
  });

  const [results, setResults] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const cropTypes = [
    { value: 'cannabis', label: 'Cannabis' },
    { value: 'leafy-greens', label: 'Leafy Greens' },
    { value: 'herbs', label: 'Herbs' },
    { value: 'tomatoes', label: 'Tomatoes' },
    { value: 'peppers', label: 'Peppers' },
    { value: 'cucumbers', label: 'Cucumbers' },
    { value: 'ornamentals', label: 'Ornamental Plants' },
    { value: 'mixed-vegetables', label: 'Mixed Vegetables' }
  ];

  const growingSystems = [
    { value: 'indoor', label: 'Indoor/Controlled Environment' },
    { value: 'greenhouse', label: 'Greenhouse' },
    { value: 'outdoor', label: 'Outdoor Field' },
    { value: 'hydroponic', label: 'Hydroponic System' },
    { value: 'soil-based', label: 'Soil-Based Growing' }
  ];

  const regionClimates = [
    { value: 'temperate', label: 'Temperate (Moderate)' },
    { value: 'humid-subtropical', label: 'Humid Subtropical' },
    { value: 'arid', label: 'Arid/Desert' },
    { value: 'tropical', label: 'Tropical' },
    { value: 'continental', label: 'Continental' }
  ];

  const pestOptions = [
    { value: 'spider-mites', label: 'Spider Mites' },
    { value: 'aphids', label: 'Aphids' },
    { value: 'thrips', label: 'Thrips' },
    { value: 'whiteflies', label: 'Whiteflies' },
    { value: 'fungus-gnats', label: 'Fungus Gnats' },
    { value: 'powdery-mildew', label: 'Powdery Mildew' },
    { value: 'botrytis', label: 'Botrytis (Gray Mold)' },
    { value: 'root-rot', label: 'Root Rot' },
    { value: 'caterpillars', label: 'Caterpillars' },
    { value: 'scale-insects', label: 'Scale Insects' }
  ];

  const budgetRanges = [
    { value: 'low', label: 'Low Budget (<$500/month)' },
    { value: 'medium', label: 'Medium Budget ($500-2000/month)' },
    { value: 'high', label: 'High Budget (>$2000/month)' },
    { value: 'unlimited', label: 'Cost Not a Primary Concern' }
  ];

  const certificationOptions = [
    { value: 'organic', label: 'Organic Certification' },
    { value: 'omri', label: 'OMRI Listed Products Only' },
    { value: 'gmp', label: 'Good Manufacturing Practices' },
    { value: 'state-compliance', label: 'State Regulatory Compliance' },
    { value: 'none', label: 'No Special Requirements' }
  ];

  const harvestSchedules = [
    { value: 'continuous', label: 'Continuous Harvest' },
    { value: 'seasonal', label: 'Seasonal Cycles' },
    { value: 'batch', label: 'Batch Production' },
    { value: 'custom', label: 'Custom Schedule' }
  ];

  const experienceLevels = [
    { value: 'beginner', label: 'Beginner (New to IPM)' },
    { value: 'intermediate', label: 'Intermediate (Some Experience)' },
    { value: 'advanced', label: 'Advanced (Professional Level)' },
    { value: 'expert', label: 'Expert (IPM Specialist)' }
  ];

  const generateIPMPlan = () => {
    setIsGenerating(true);
    
    setTimeout(() => {
      const plan = {
        planId: `IPM-${Date.now()}`,
        preventiveStrategy: generatePreventiveStrategy(),
        monitoringProtocol: generateMonitoringProtocol(),
        treatmentProtocols: generateTreatmentProtocols(),
        beneficialIntegration: generateBeneficialStrategy(),
        scheduleCalendar: generateScheduleCalendar(),
        costAnalysis: generateCostAnalysis(),
        complianceNotes: generateComplianceNotes(),
        implementationTimeline: generateImplementationTimeline()
      };

      setResults(plan);
      
      // Save to history
      onCalculationComplete({
        cropType: formData?.cropType,
        growingSystem: formData?.growingSystem,
        planId: plan?.planId,
        estimatedCost: plan?.costAnalysis?.monthlyTotal,
        preventiveActions: plan?.preventiveStrategy?.length
      });

      setIsGenerating(false);
    }, 2000);
  };

  const generatePreventiveStrategy = () => {
    const strategies = [];
    
    strategies?.push({
      category: 'Environmental Controls',
      actions: [
        'Maintain optimal temperature (65-75°F)',
        'Control humidity (45-65% RH)',
        'Ensure proper air circulation',
        'Regular facility sanitation'
      ]
    });

    if (formData?.currentPests?.includes('fungus-gnats') || formData?.growingSystem === 'soil-based') {
      strategies?.push({
        category: 'Soil Management',
        actions: [
          'Allow soil surface to dry between waterings',
          'Use yellow sticky traps near soil surface',
          'Apply beneficial nematodes preventively'
        ]
      });
    }

    strategies?.push({
      category: 'Plant Health',
      actions: [
        'Weekly plant inspections',
        'Proper nutrition management',
        'Stress reduction protocols',
        'Quarantine procedures for new plants'
      ]
    });

    return strategies;
  };

  const generateMonitoringProtocol = () => {
    return {
      frequency: formData?.experienceLevel === 'beginner' ? 'Daily' : 'Every 2-3 days',
      checkpoints: [
        'Visual inspection of leaves (top and bottom)',
        'Check soil moisture and drainage',
        'Monitor environmental conditions',
        'Document pest pressure levels (1-5 scale)',
        'Photograph any issues for tracking'
      ],
      tools: [
        'Hand lens or magnifying glass',
        'Sticky traps (yellow and blue)',
        'pH and EC meters',
        'Temperature/humidity logger',
        'Smartphone camera for documentation'
      ]
    };
  };

  const generateTreatmentProtocols = () => {
    const protocols = [];

    formData?.currentPests?.forEach(pest => {
      let protocol = {};
      
      switch(pest) {
        case 'spider-mites':
          protocol = {
            pest: 'Spider Mites',
            threshold: 'Action at 2+ mites per leaf',
            treatments: [
              { week: 1, action: 'Release Phytoseiulus persimilis', rate: '2-5 per plant' },
              { week: 1, action: 'Foliar spray with neem oil', rate: '1-2 oz/gallon' },
              { week: 2, action: 'Follow-up beneficial release', rate: '1-2 per plant' }
            ]
          };
          break;
        case 'aphids':
          protocol = {
            pest: 'Aphids',
            threshold: 'Action at 5+ aphids per plant',
            treatments: [
              { week: 1, action: 'Release Aphidius colemani', rate: '0.5-1 per plant' },
              { week: 1, action: 'Soap spray application', rate: '2-4 oz/gallon' },
              { week: 2, action: 'Beneficial top-up release', rate: '0.25 per plant' }
            ]
          };
          break;
        default:
          protocol = {
            pest: pest?.replace('-', ' ')?.replace(/\b\w/g, l => l?.toUpperCase()),
            threshold: 'Action at first sign of presence',
            treatments: [
              { week: 1, action: 'Targeted treatment application', rate: 'As per label' },
              { week: 2, action: 'Follow-up monitoring', rate: 'N/A' }
            ]
          };
      }
      
      protocols?.push(protocol);
    });

    return protocols;
  };

  const generateBeneficialStrategy = () => {
    return {
      primaryBeneficials: [
        { species: 'Amblyseius cucumeris', target: 'Thrips prevention', releaseRate: '25,000 per 1,000 sq ft' },
        { species: 'Stratiolaelaps scimitus', target: 'Fungus gnat control', releaseRate: '10,000 per 1,000 sq ft' }
      ],
      releaseSchedule: [
        { week: 2, action: 'Initial preventive release', species: 'Multiple' },
        { week: 6, action: 'Mid-cycle refresh', species: 'Target-specific' },
        { week: 10, action: 'Maintenance release', species: 'As needed' }
      ],
      compatibilityNotes: [
        'Avoid harsh chemicals 48-72 hours before/after releases',
        'Maintain moderate humidity for beneficial survival',
        'Provide refugia (shelter plants) when possible'
      ]
    };
  };

  const generateScheduleCalendar = () => {
    const schedule = [];
    
    for (let week = 1; week <= 12; week++) {
      const weeklyTasks = [];
      
      // Monitoring (every week)
      weeklyTasks?.push('Visual plant inspection');
      
      // Beneficial releases
      if ([2, 6, 10]?.includes(week)) {
        weeklyTasks?.push('Beneficial insect release');
      }
      
      // Environmental checks
      if (week % 2 === 0) {
        weeklyTasks?.push('Environmental parameter check');
      }
      
      // Treatment applications (as needed)
      if (week % 3 === 1) {
        weeklyTasks?.push('Preventive treatment (if indicated)');
      }

      schedule?.push({
        week,
        tasks: weeklyTasks,
        priority: week <= 4 ? 'High' : 'Medium'
      });
    }
    
    return schedule;
  };

  const generateCostAnalysis = () => {
    const budgetMultipliers = {
      'low': 0.5,
      'medium': 1.0,
      'high': 2.0,
      'unlimited': 3.0
    };

    const baseCosts = {
      monitoring: 150,
      beneficials: 300,
      treatments: 200,
      equipment: 100
    };

    const multiplier = budgetMultipliers?.[formData?.budgetRange] || 1.0;
    
    return {
      monitoring: Math.round(baseCosts?.monitoring * multiplier),
      beneficials: Math.round(baseCosts?.beneficials * multiplier),
      treatments: Math.round(baseCosts?.treatments * multiplier),
      equipment: Math.round(baseCosts?.equipment * multiplier),
      monthlyTotal: Math.round((baseCosts?.monitoring + baseCosts?.beneficials + baseCosts?.treatments + baseCosts?.equipment) * multiplier)
    };
  };

  const generateComplianceNotes = () => {
    const notes = [];
    
    if (formData?.certificationRequirements?.includes('organic')) {
      notes?.push('All products must be OMRI listed or NOP compliant');
      notes?.push('Maintain detailed application records');
    }
    
    if (formData?.cropType === 'cannabis') {
      notes?.push('Follow state-specific cannabis regulations');
      notes?.push('Consider residue testing requirements');
    }
    
    notes?.push('Always follow product label instructions');
    notes?.push('Maintain proper REI and PHI intervals');
    
    return notes;
  };

  const generateImplementationTimeline = () => {
    return [
      { phase: 'Week 1-2', title: 'Setup & Baseline', tasks: ['Install monitoring equipment', 'Establish baseline pest levels', 'Initial environmental optimization'] },
      { phase: 'Week 3-4', title: 'Prevention Launch', tasks: ['First beneficial releases', 'Implement monitoring protocols', 'Staff training'] },
      { phase: 'Week 5-8', title: 'Active Management', tasks: ['Regular monitoring and adjustments', 'Treatment interventions as needed', 'Data collection and analysis'] },
      { phase: 'Week 9-12', title: 'Optimization', tasks: ['Refine protocols based on results', 'Long-term planning', 'Cost-effectiveness review'] }
    ];
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setResults(null);
  };

  const handleArrayFieldChange = (field, value, checked) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked 
        ? [...prev?.[field], value]
        : prev?.[field]?.filter(item => item !== value)
    }));
    setResults(null);
  };

  const resetForm = () => {
    setFormData({
      cropType: '',
      growingSystem: 'indoor',
      facilitySize: '',
      regionClimate: 'temperate',
      currentPests: [],
      historicalIssues: [],
      budgetRange: 'medium',
      certificationRequirements: [],
      harvestSchedule: 'continuous',
      experienceLevel: 'intermediate'
    });
    setResults(null);
  };

  const isFormValid = formData?.cropType && formData?.facilitySize;

  return (
    <div className="space-y-6">
      {/* Input Form */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Crop Type *
            </label>
            <Select
              value={formData?.cropType}
              onChange={(value) => handleInputChange('cropType', value)}
              options={cropTypes}
              placeholder="Select primary crop"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Growing System *
            </label>
            <Select
              value={formData?.growingSystem}
              onChange={(value) => handleInputChange('growingSystem', value)}
              options={growingSystems}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Facility Size (sq ft) *
            </label>
            <Input
              type="number"
              value={formData?.facilitySize}
              onChange={(e) => handleInputChange('facilitySize', e?.target?.value)}
              placeholder="Enter total growing area"
              min="0"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Regional Climate
            </label>
            <Select
              value={formData?.regionClimate}
              onChange={(value) => handleInputChange('regionClimate', value)}
              options={regionClimates}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Experience Level
            </label>
            <Select
              value={formData?.experienceLevel}
              onChange={(value) => handleInputChange('experienceLevel', value)}
              options={experienceLevels}
            />
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Current/Historical Pest Issues
            </label>
            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
              {pestOptions?.map(pest => (
                <div key={pest?.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={pest?.value}
                    checked={formData?.currentPests?.includes(pest?.value)}
                    onChange={(checked) => handleArrayFieldChange('currentPests', pest?.value, checked)}
                  />
                  <label htmlFor={pest?.value} className="text-sm text-gray-700">
                    {pest?.label}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Budget Range
            </label>
            <Select
              value={formData?.budgetRange}
              onChange={(value) => handleInputChange('budgetRange', value)}
              options={budgetRanges}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Certification Requirements
            </label>
            <div className="space-y-2">
              {certificationOptions?.map(cert => (
                <div key={cert?.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={cert?.value}
                    checked={formData?.certificationRequirements?.includes(cert?.value)}
                    onChange={(checked) => handleArrayFieldChange('certificationRequirements', cert?.value, checked)}
                  />
                  <label htmlFor={cert?.value} className="text-sm text-gray-700">
                    {cert?.label}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Harvest Schedule
            </label>
            <Select
              value={formData?.harvestSchedule}
              onChange={(value) => handleInputChange('harvestSchedule', value)}
              options={harvestSchedules}
            />
          </div>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <Button
          variant="outline"
          onClick={resetForm}
          className="border-gray-300"
        >
          Reset Form
        </Button>
        
        <Button
          onClick={generateIPMPlan}
          disabled={!isFormValid || isGenerating}
          className="bg-purple-600 hover:bg-purple-700 text-white"
        >
          {isGenerating ? (
            <>
              <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
              Generating Plan...
            </>
          ) : (
            <>
              <Icon name="Settings" size={16} className="mr-2" />
              Generate IPM Plan
            </>
          )}
        </Button>
      </div>
      {/* Results */}
      {results && (
        <div className="mt-6 space-y-6">
          {/* Plan Header */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <Icon name="FileText" size={24} className="text-purple-600" />
                <div>
                  <h3 className="text-xl font-bold text-purple-900">Custom IPM Plan</h3>
                  <p className="text-purple-700">Plan ID: {results?.planId}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-purple-600">${results?.costAnalysis?.monthlyTotal}/mo</div>
                <div className="text-sm text-purple-700">Estimated Monthly Cost</div>
              </div>
            </div>
          </div>

          {/* Preventive Strategy */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Icon name="Shield" size={20} className="text-green-600 mr-2" />
              Preventive Strategy
            </h4>
            <div className="space-y-4">
              {results?.preventiveStrategy?.map((strategy, index) => (
                <div key={index}>
                  <h5 className="font-medium text-gray-900 mb-2">{strategy?.category}</h5>
                  <ul className="space-y-1 ml-4">
                    {strategy?.actions?.map((action, actionIndex) => (
                      <li key={actionIndex} className="text-sm text-gray-700 flex items-center">
                        <Icon name="ChevronRight" size={14} className="text-gray-400 mr-2" />
                        {action}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Monitoring Protocol */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Icon name="Eye" size={20} className="text-blue-600 mr-2" />
              Monitoring Protocol
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h5 className="font-medium text-gray-900 mb-2">Inspection Frequency: {results?.monitoringProtocol?.frequency}</h5>
                <h6 className="font-medium text-gray-700 mb-2">Checkpoints:</h6>
                <ul className="space-y-1">
                  {results?.monitoringProtocol?.checkpoints?.map((checkpoint, index) => (
                    <li key={index} className="text-sm text-gray-600 flex items-start">
                      <Icon name="Check" size={14} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      {checkpoint}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h6 className="font-medium text-gray-700 mb-2">Required Tools:</h6>
                <ul className="space-y-1">
                  {results?.monitoringProtocol?.tools?.map((tool, index) => (
                    <li key={index} className="text-sm text-gray-600 flex items-center">
                      <Icon name="Tool" size={14} className="text-gray-400 mr-2" />
                      {tool}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Cost Breakdown */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Icon name="DollarSign" size={20} className="text-green-600 mr-2" />
              Cost Analysis (Monthly)
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-xl font-bold text-gray-900">${results?.costAnalysis?.monitoring}</div>
                <div className="text-sm text-gray-600">Monitoring</div>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-xl font-bold text-gray-900">${results?.costAnalysis?.beneficials}</div>
                <div className="text-sm text-gray-600">Beneficials</div>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-xl font-bold text-gray-900">${results?.costAnalysis?.treatments}</div>
                <div className="text-sm text-gray-600">Treatments</div>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-xl font-bold text-gray-900">${results?.costAnalysis?.equipment}</div>
                <div className="text-sm text-gray-600">Equipment</div>
              </div>
            </div>
          </div>

          {/* Implementation Timeline */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Icon name="Calendar" size={20} className="text-purple-600 mr-2" />
              Implementation Timeline
            </h4>
            <div className="space-y-4">
              {results?.implementationTimeline?.map((phase, index) => (
                <div key={index} className="border-l-4 border-purple-200 pl-4">
                  <h5 className="font-medium text-gray-900">{phase?.phase}: {phase?.title}</h5>
                  <ul className="mt-2 space-y-1">
                    {phase?.tasks?.map((task, taskIndex) => (
                      <li key={taskIndex} className="text-sm text-gray-600 flex items-center">
                        <Icon name="Circle" size={8} className="text-purple-400 mr-2" />
                        {task}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IPMPlanGenerator;